import React, { useEffect, useMemo, useState } from 'react';
import type { Highlight } from './Label';
import { forcedSelectOption } from '../../../../utils/forcedSelectOption';

export type SelectOption = string | { value: string; label: string };

type Props = {
  value?: string;
  onChange?: (value: string) => void;
  options: SelectOption[];
  placeholder?: string;
  highlight?: Highlight;
  width?: number;
  disabled?: boolean;
  className?: string;
  ariaLabel?: string;
};

const baseSelect =
  'min-w-0 bg-cmp-field ' +
  'border border-cmp-field-edge rounded ' +
  'px-1 py-[0.125rem] text-[0.78125rem] text-cmp-field-fg h-6 cursor-pointer';

/**
 * Compact `<select>` styled to match TextInput. Accepts either string
 * options or {value,label} option objects.
 */
const SelectInput: React.FC<Props> = ({
  value,
  onChange,
  options,
  placeholder,
  highlight,
  width,
  disabled,
  className,
  ariaLabel,
}) => {
  const [v, setV] = useState(value ?? '');
  useEffect(() => setV(value ?? ''), [value]);

  // `!`-prefixed (Tailwind important) so the highlight wins over baseSelect's
  // dark: variants — `dark:bg-slate-900` has specificity (0,2,0) which would
  // otherwise beat `bg-red-100` (0,1,0) under .dark.
  const hl =
    highlight === 'yellow'
      ? '!bg-yellow-50 dark:!bg-yellow-900/30 !border-yellow-500'
      : highlight === 'red'
        ? '!bg-red-100 dark:!bg-red-900/40 !border-red-600 dark:!border-red-800 !text-red-800 dark:!text-red-200 font-semibold'
        : '';
  const flexCls = width ? 'flex-none' : 'flex-1';
  const disabledCls = disabled ? 'opacity-60 cursor-not-allowed bg-slate-100 dark:bg-gray-800' : '';

  // Stale-value cue: when the stored value isn't among the current options (e.g. a saved
  // dispatcher who's since been deactivated), render it so it still shows AND flag it orange so
  // the user can spot it and pick a valid value. Guarded on `hasRealOptions` so a value doesn't
  // flash orange while the option list is still loading; an explicit `highlight` still wins.
  const optionValues = useMemo(
    () => new Set(options.map((o) => (typeof o === 'string' ? o : o.value))),
    [options],
  );
  const hasRealOptions = useMemo(
    () => options.some((o) => (typeof o === 'string' ? o : o.value) !== ''),
    [options],
  );
  const isStale = !!v && hasRealOptions && !optionValues.has(v);
  const staleHl =
    isStale && !highlight
      ? '!bg-orange-50 dark:!bg-orange-900/20 !border-orange-400 dark:!border-orange-500'
      : '';

  // The BLANK half of the same problem the stale cue above solves. `isStale` is
  // guarded on `!!v`, so a blank value whose option list has no blank row fell
  // through — and an HTML select whose `value` matches nothing renders the FIRST
  // option, so a blank silently displayed as 'N'/'Y'/whatever came first. That
  // reads as already-set, and since re-picking the shown option fires no change
  // event, nothing stages and the blank never self-heals. Force a blank row so
  // the field shows what it actually holds (V1 smod_forcevalueinarray). A
  // placeholder already supplies that row, so skip when one is present.
  const forcedBlank = !placeholder && forcedSelectOption(v, options)?.value === '';

  return (
    <select
      value={v}
      disabled={disabled}
      aria-label={ariaLabel}
      title={isStale ? 'This value is no longer an active choice — verify or update it.' : undefined}
      onChange={(e) => {
        setV(e.target.value);
        onChange?.(e.target.value);
      }}
      className={`${baseSelect} ${hl} ${staleHl} ${flexCls} ${disabledCls} ${className ?? ''}`}
      style={width ? { width: `${width / 16}rem` } : undefined}
    >
      {placeholder && <option value="">{placeholder}</option>}
      {forcedBlank && <option value="" />}
      {isStale && <option value={v}>{v}</option>}
      {options.map((o, i) => {
        const val = typeof o === 'string' ? o : o.value;
        const lbl = typeof o === 'string' ? o : o.label;
        // A placeholder already supplies the empty/unselected row, so drop any
        // explicit blank option to avoid rendering two blank rows.
        if (placeholder && val === '') return null;
        return (
          <option key={i} value={val}>
            {lbl}
          </option>
        );
      })}
    </select>
  );
};

export default SelectInput;
